// src/utils/image-thumbnail.ts
/**
 * Genera una miniatura JPEG di piccole dimensioni da un File immagine, lato client,
 * per anteprime rapide delle card Kanban. Restituisce null in caso di qualsiasi errore:
 * i chiamanti devono proseguire con l'upload senza miniatura, non bloccarlo.
 */
export async function createImageThumbnail(
  file: File,
  maxSize = 480,
  quality = 0.8
): Promise<File | null> {
  try {
    let bitmap: ImageBitmap | HTMLImageElement;
    let width: number;
    let height: number;

    if (typeof createImageBitmap === 'function') {
      // Le opzioni di orientamento EXIF ('from-image') non sono ancora nei tipi DOM di TS.
      bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
      width = bitmap.width;
      height = bitmap.height;
    } else {
      const url = URL.createObjectURL(file);
      try {
        bitmap = await new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url;
        });
        width = bitmap.width;
        height = bitmap.height;
      } finally {
        URL.revokeObjectURL(url);
      }
    }

    const scale = Math.min(1, maxSize / Math.max(width, height));
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, targetW, targetH);
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);

    if ('close' in bitmap) {
      bitmap.close();
    }

    const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob) return null;

    const baseName = file.name.replace(/\.[^.]+$/, '');
    return new File([blob], `thumb_${baseName}.jpg`, { type: 'image/jpeg' });
  } catch {
    return null;
  }
}
